<template>
<div>

	<div v-if="$store.accountSetupMode" class="alert alert-info">
		<h2>Welcome!</h2>

		<p>This is your account profile page. Please take a moment to fill in your personal details, and perhaps upload a picture of yourself so people can see who you are.</p>

		<p>You can always find your way back here by clicking 'Profile' in the top menu.</p>

		<p>When you're happy with your personal profile, click the button below and we'll move onto what's next.</p>

		<router-link class="btn btn-light" :to="'/employee/'+$route.params.id+'/account-settings'">
			<i class="fa fa-check"></i>
			Ok, Next Steps
		</router-link>
	</div>

	<div class="row" v-if="$store.employee">
		<div class="col-lg-4 col-md-4 text-center">
			<widget>
				<i v-if="!$store.profileImage" class="fa fa-user fa-5x mb-2"></i>

				<upload-field-with-panel
					api-model="ProfileImage"
					upload-type="image"
					upload-message="Upload a profile photo"
					v-model="$store.profileImage"
					v-on:uploaded="imageUploaded"
					v-on:deleted="profileImageDeleted"
					:readonly="readonly">
				</upload-field-with-panel>

				<div v-if="!baseDetailsOnly"> 
					<last-active-indicator 
						:last-active-utc="$store.employee && $store.employee.last_active"
						:user-id="$store.employee.id">
					</last-active-indicator>
					Last active {{formatDateTimeToLocal($store.employee.last_active)}}
				</div>
			</widget>

			<template v-if="!baseDetailsOnly">
				<widget v-if="($store.employee.id == $store.userDetails.id || hasModulePermRead('Users')) && eventTotals.numSickDaysUsed !== false">
					<p>Since {{resetDate.format("MMM, D, YYYY")}}</p>
					<table class="days-off-table table table-sm">
						<tr class="sick-days">
							<th>Sick Days:</th>
							<td>{{eventTotals.numSickDaysUsed}}</td>
						</tr>
						<tr class="sick-days">
							<th>Partial Sick Days:</th>
							<td>{{eventTotals.partialSickDayHrsUsed}} hrs over {{eventTotals.numPartialSickDays}} days</td>
						</tr>
						<tr class="vacation-days">
							<th>Vacation Days Used:</th>
							<td>{{eventTotals.numVacationUsed}}</td>
						</tr>
						<tr class="vacation-days">
							<th>Vacation Days Scheduled:</th>
							<td>{{eventTotals.numVacationScheduled}}</td>
						</tr>
						<tr class="vacation-days">
							<th>Vacation Days Remaining:</th>
							<td>{{numVacationRemaining}}</td>
						</tr>
					</table>
				</widget>
			</template>

		</div>
		<div class="col-lg-8 col-md-8">
			
			<vue-form v-on:submit="saveDetails" :working="statusWorking" :readonly="readonly" :no-top-button="true" :no-bottom-button="true">
				<widget class="form">
					<h2 style="margin-bottom: 0;">{{ $store.employee.first_name }} {{ $store.employee.last_name }}
						<template v-if="unionMembership">
							<br>
							<small class="text-muted">Member of {{unionMembership}} Union</small>
						</template>
					</h2>

				</widget>
				<widget class="form">
					<h3>Personal Details</h3>
		
					<field label="First Name" col-class="col-md-3" v-model="$store.employee.first_name"  :readonly="readonly" v-on:change="saveDetails"></field>

					<field label="Last Name" col-class="col-md-3" v-model="$store.employee.last_name"  :readonly="readonly" v-on:change="saveDetails"></field>

					<field v-if="!readonly || readonly && $store.employee.email" label="Email" col-class="col-md-3" v-model="$store.employee.email" :readonly="readonly" v-on:change="saveDetails"></field>

					<field label="Job Title" col-class="col-md-3" v-model="$store.employee.job_title" :readonly="readonly" v-on:change="saveDetails"></field>

					<field label="About" col-class="col-md-3" v-model="$store.employee.description" :params="{type:'textarea'}" :readonly="readonly" v-on:change="saveDetails"></field>

					<field v-if="!baseDetailsOnly"
						label="Birthdate" 
						col-class="col-md-3" 
						v-model="$store.employee.birthdate"
						:params="{type: 'date'}" 
						:readonly="readonly" 
						v-on:change="saveDetails">
					</field>

					<template v-if="!baseDetailsOnly && isActiveWithEmployer">
						<field label="Phone: Home" 
							col-class="col-md-3" 
							v-model="$store.employee.phone" 
							:readonly="readonly" 
							v-on:change="saveDetails">
						</field>
						<field label="Phone: Mobile" 
							col-class="col-md-3" 
							v-model="$store.employee.phone_mobile" 
							:readonly="readonly" 
							v-on:change="saveDetails">
						</field>
					</template>

					<field v-if="$store.employee.id == $store.userDetails.id"
						label="I would like other employers and users to find me"
						col-class="col-sm-9 col-md-10"
						:control-left="true"
						:params="{type:'boolean'}"
						v-model="$store.employee.is_looking_for_work"
						v-on:change="saveDetails">
					</field>

				</widget>

				<template v-if="!baseDetailsOnly && isActiveWithEmployer">
				
					<widget class="form">
						<h3>Address</h3>

						<field label="Address" col-class="col-md-3" v-model="$store.employee.address"  :readonly="readonly" v-on:change="saveDetails"></field>
						<field label="City" col-class="col-md-3" v-model="$store.employee.city"  :readonly="readonly" v-on:change="saveDetails"></field>
						<field  label="Province/State" col-class="col-md-3" v-model="$store.employee.province" :params="{type:'province'}" :readonly="readonly" v-on:change="saveDetails"></field>
						<field label="Country" col-class="col-md-3" v-model="$store.employee.country"  :readonly="readonly" v-on:change="saveDetails"></field>
						<field :label="statesList.find(state => state.value == $store.employee.province) ? 'Zip Code' : 'Postal Code'" col-class="col-md-3" v-model="$store.employee.postal_code"  :readonly="readonly" v-on:change="saveDetails"></field>
					</widget>

					<widget class="form">
						<h3>Emergency Contact</h3>

						<field label="Emergency Contact Name" col-class="col-md-3" v-model="$store.employee.emergency_contact_name"  :readonly="readonly" v-on:change="saveDetails"></field>
						<field label="Emergency Contact Phone" col-class="col-md-3" v-model="$store.employee.emergency_contact_phone"  :readonly="readonly" v-on:change="saveDetails"></field>
						<field label="Emergency Contact Address" col-class="col-md-3" v-model="$store.employee.emergency_contact_address" :params="{type: 'textarea'}" :readonly="readonly" v-on:change="saveDetails"></field>
						<field label="Emergency Contact Relationship" col-class="col-md-3" v-model="$store.employee.emergency_contact_relationship" :params="{type: 'text'}" :readonly="readonly" v-on:change="saveDetails"></field>
					</widget>
				</template>

			</vue-form>
		</div>
	</div>
	<hr>
	<button v-if="!showWorkHistory" class="btn btn-light" v-on:click="fetchWorkHistory">
		View Work History
	</button>
	<div v-if="showWorkHistory">
		<h3 class="mt-4" style="margin-bottom: 0;">
			Work History
		</h3>
		<p class="text-muted">Current as of {{ moment(workHistoryDate).format("MMM D, Y") }}</p>

		<div v-for="entry in workHistory" class="row mb-2">
			<div class="col-sm-6 col-md-4">
				<h5 v-if="entry.jobName == 'Various Jobs'">
					{{entry.companyName}}<br>
					<small><em>Various Jobs</em></small>
				</h5>
				<h5 v-else>
					{{entry.jobName}}<br>
					<small>by <em>{{entry.companyName}}</em></small>
				</h5>
				
			</div>
			<div class="col-sm-6 col-md-8">
				<div class="card">
					<div class="card-body">
						<div>
							<strong>{{moment(entry.startDate).format('MMM D, Y')}} - {{moment(entry.endDate).format('MMM D, Y')}}</strong>
							<span v-if="entry.role">, <em>{{entry.role}}</em></span>
						</div>
						<p>Worked {{entry.totalHrs}} hours over {{entry.totalDays}} days as 
							a<span v-if="entry.classification"><em> {{entry.classification}}</em></span>
							<span v-else>n <em>unclassified</em> employee</span>
							<span v-if="entry.jobCategory"> performing <em> {{entry.jobCategory}}</em> work</span>
						</p>
						<div v-if="entry.companyType">
							<em>{{entry.companyName}}</em> is {{entry.companyType.firstLetterIsVowel() ? 'an' : 'a'}} <em>{{entry.companyType}}</em> company
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
	<hr>
	<div v-if="!baseDetailsOnly && ($store.userRole == 'Admin' || hasModulePermRead('Users'))">

		<h3>Project History</h3>

		<table-list
			record-name="Job"
			:fields="jobHistoryFields"
			:records="mutatedEmployeeJobs">

			<p>A list of all jobs the user is currently assigned to:</p>

		</table-list>

	</div>

</div>

</template>

<script>

import VueForm from "@/components/VueForm"
import UploadFieldWithPanel from "@/components/super_components/UploadFieldWithPanel"
import FormGroup from "@/components/FormGroup"
import Field from "@/components/Field"
import TableList from "@/components/TableList"
import LastActiveIndicator from "@/components/LastActiveIndicator"
import Widget from "@/components/Widget"

import EventsMixins from "@/mixins/EventsMixins.js"
import ScheduleMixins from "@/mixins/ScheduleMixins.js"

export default {

	name: 'EmployeeDetails',
	components: {
		FormGroup,
		Field,
		UploadFieldWithPanel,
		VueForm,
		TableList,
		LastActiveIndicator,
		Widget
	},
	mixins: [EventsMixins,ScheduleMixins],
	props: ["blockFetches"],
	data() {
		return {
			statusWorking: false,
			jobHistoryFields: [
				{label: "Project #",key:"jobNumLinked",type:"link"},
				{label: "Project Name",key:"jobNameLinked",type:"link"},
				{label: "Project City",key:"city"},
				{label: "Project Status",key:"jobStatus"},
				{label: "Project Landed",key:"date_landed"},
				{label: "Role Assigned",key:"assignedRole"},
				{label: "Date Role Assigned",key:"dateAssigned"}
			],
			employeeJobs: [],
			showWorkHistory: false,
			workHistory: [],
			workHistoryDate: null,
		}
	},
	computed: {
		numVacationRemaining() {
			if ("EmployeeSetting" in this.$store.employee && this.$store.employee.EmployeeSetting && this.$store.employee.EmployeeSetting.length > 0)
				return +this.$store.employee.EmployeeSetting[0].num_annual_vacation_days - +this.eventTotals.numVacationUsed

			return "Vacation not allocated"
		},
		unionMembership() {
			if (this.$store.employee && "Union" in this.$store.employee && this.$store.employee.Union)
				return this.$store.employee.Union.union_name

			return false
		},
		baseDetailsOnly() {
			if ("address" in this.$store.employee)
				return false
			else
				return true
		},
		isActiveWithEmployer() {
			if (this.$store.employee && 'id' in this.$store.employee && 'UserPermission' in this.$store.employee && this.$store.employee.UserPermission.length > 0 && this.$store.employee.UserPermission[0].is_inactive == 0)
				return true
			else
				return false
		},
		readonly() {

			//check if account is set to read only
			if (this.$store.employee && this.$store.employee.editing_locked == 1 && this.$store.userDetails.slug != this.$store.employee.slug)
				return true

			let modulePerms = this.getUserModulePerms()

			if (!this.isActiveWithEmployer) {
				if (this.$route.params.id == this.$store.userDetails.slug)
					return false
				else
					return true
			}

			if (this.$route.params.id == this.$store.userDetails.slug)
				return false
			else if (this.getUserRoleType() == "Admin")
				return false
			else if (this.hasModulePermWrite("Users"))
				return false
			else
				return true
		},
		mutatedEmployeeJobs() {
			let vm = this

			return this.employeeJobs.map(job => {

				job.JobPersonnelRole.forEach(role => {
					if (role.user_id == vm.$store.employee.id) {
						job.assignedRole = role.title
						job.dateAssigned = vm.formatDate(role.updated_at)
					}
				})

				job.jobStatus = "Active"
				if (job.archived == 1)
					job.jobStatus = "Archived"

				job.jobNumLinked = {
					text: job.job_num,
					link: "/project/"+job.slug+"/dashboard"
				}

				job.jobNameLinked = {
					text: job.job_name,
					link: "/project/"+job.slug+"/dashboard"
				}

				return job
			})
		}
	},
	methods: {
		imageUploaded(response) {

			this.$store.profileImageUrl = this.$root.apiRoot+'resources/profile-images/'+response.data.slug+'/'+response.data.filename

			this.$store.employee.image_id = response.data.id

			this.saveDetails()
		},
		rotateImage(dir) {
			let payload = {
				id: this.$store.employee.image_id,
				rotate_direction: dir
			}

			let vm = this

			this.postData("ProfileImage/rotate",payload).then(response => {
				//strip query string, and then add another random one

				let imageUrl = vm.$store.profileImageUrl.split("?")[0]

				vm.$store.profileImageUrl = imageUrl+"?"+Math.random()

				vm.$forceUpdate()
			})
		},
		profileImageDeleted() {
			let vm = this
			this.$store.employee.image_id = 0
			this.$store.employee.ProfileImage = null

			this.postData("User/save",{
				id: vm.$store.employee.id,
				image_id: 0
			})
		},
		saveDetails() {
			let vm = this
			
			this.$nextTick(() => {

				vm.statusWorking = true

				let payload = JSON.parse(JSON.stringify(vm.$store.employee))

				let deleteKeys = [
					"editing_locked",
					"UserPermission",
					"ProfileImage",
					"last_login",
					"last_active",
					"invite_accepted",
					"clocked_in",
					"created_by",
					"created_at",
					"EmployeeClassification",
					"one_signal_user_id",
					"send_marketing_emails",
					"send_service_request_emails",
					"tenant_id",
					"id",
					"widget_settings_json",
					"EmployeeSetting",
					"email_verified",
					"admin_lockout",
					"has_push_notification_devices",
					"public_phone",
					"public_email",
					"channel_partner_id",
					"invited_by",
					"last_active_domain"
				]

				deleteKeys.forEach(key => {
					delete payload[key]
				})

				if (vm.$store.accountSetupMode)
					payload.invite_accepted = 1

				vm.postData("User/save",payload).then(function(response){

					vm.statusWorking = false

					if (response.success) {	

						if (vm.$store.employee.slug == vm.$store.userDetails.slug) {

							//sync $store.userDetails with $store.employee
							let fields = [
								"first_name",
								"last_name",
								"email",
								"address",
								"city",
								"province",
								"country",
								"postal_code",
								"emergency_contact_address",
								"emergency_contact_phone",
								"emergency_contact_name",
								"is_looking_for_work"
							]

							fields.forEach(field => {
								vm.$store.userDetails[field] = vm.$store.employee[field]
							})
						}

						if (vm.$route.params.id == "new") {
							vm.$router.push("/employee/"+response.slug+"/edit")

							vm.$store.employee.id = response.slug
							vm.alertSaved("New user created! A welcome email with their login credentials has been sent.")
						}
						else
							vm.alertSaved("Saved!")

					}
					else {
						if ("errors" in response)
							vm.alertErrors(response.errors)
					}
				})
			})
		},
		fetchWorkHistory() {
			let vm = this

			this.spinner(true)
			this.getJSON("WorkHistory/get",{filters:[['user_id',vm.$store.employee.id]]}).then(response => {
				vm.spinner(false)

				if (response.success) {

					let history = []

					let miscJobs = []

					let historyObj = []
					try {
						historyObj = JSON.parse(response.data[0].work_history_json)
					}
					catch (ex) {}

					vm.workHistoryDate = response.data.last_updated

					historyObj.forEach(entry => {
						if (parseFloat(entry.totalHrs) > 100) {
							//add to history as its own entry
							history.push(entry)
						}
						else {
							//add it to miscelaneous, organized by company
							let companyFound = false
							let companyIndex = miscJobs.findIndex(company => company.tenant_id == entry.tenant_id)

							if (companyIndex != -1) {
								miscJobs[companyIndex].totalDays += parseFloat(entry.totalDays)
								miscJobs[companyIndex].totalHrs += parseFloat(entry.totalHrs)

								//if job category is different, add it to a comma separated list (but check that its not already in the list first)

								if (!("jobCategory" in miscJobs[companyIndex]) || miscJobs[companyIndex].jobCategory != entry.jobCategory) {
									if (!("jobCategory" in miscJobs[companyIndex]))
										miscJobs[companyIndex].jobCategory = entry.jobCategory

									if (miscJobs[companyIndex].jobCategory && miscJobs[companyIndex].jobCategory.indexOf(entry.jobCategory) == -1 && typeof entry.jobCategory != 'undefined')
										miscJobs[companyIndex].jobCategory += ", "+entry.jobCategory
								}

								//set earliest date
								miscJobs[companyIndex].startDate = vm.moment.min(vm.moment(miscJobs[companyIndex].startDate),vm.moment(entry.startDate)).format("YYYY-MM-DD")

								//set latest date
								miscJobs[companyIndex].endDate = vm.moment.max(vm.moment(miscJobs[companyIndex].startDate),vm.moment(entry.startDate)).format("YYYY-MM-DD")

								miscJobs[companyIndex].jobName = "Various Jobs"
								miscJobs[companyIndex].role = false
							}
							else {
								miscJobs.push(entry)
							}
						}
					})

					history.reverse()

					if (miscJobs.length > 0)
						history = history.concat(miscJobs) //append miscellious jobs to end

					vm.showWorkHistory = true

					vm.workHistory = history

				}
			})
		},
		fetchEmployeeJobs() {
			let vm = this

			this.spinner(true)

			let params = {
				include: ["JobPersonnelRole"],
				filters: [
					["JobPersonnelRole.user_id",this.$store.employee.id]
				],
				orders: [
					["archived","ASC"],
					["date_landed","DESC"]
				]
			}

			this.getJSON("Job/get",params).then(response => {
				if (response.success)
					vm.employeeJobs = response.data
				
				vm.spinner(false)
			})
		},
		fetchEvents() {
			let vm = this

			let params = {
				filters: [
					["start_time >=",this.resetDate.format("YYYY-MM-DD")],
					["user_id",this.$store.employee.id]
				]
			}

			this.getJSON("ScheduleEvent/get",params).then(response => {

				let vacationUsed = 0
				let vacationScheduled = 0
				let sickUsed = 0

				if (response.success)
					vm.eventTotals = vm.calcScheduleEventTypeTotals(response.data)
				
			})
		}
	},
	watch: {
		blockFetches() {
			if (!this.blockFetches) {
				this.fetchEmployeeJobs()
				this.fetchEvents()
			}
			
		}
	},
	created() {
		if (!this.blockFetches) {
			this.fetchEmployeeJobs()
			this.fetchEvents()
		}

		this.fetchCompanyData(false,["CompanySetting"])

		if (this.$store.userDetails.first_name == "" || this.$store.userDetails.last_name == "")
			this.$store.accountSetupMode = true
	}

}
</script>

<style type="text/css">
	.sick-days {
		color: #a56218;
	}

	.vacation-days {
		color: green;
	}

	.days-off-table td {
		border: none;
	}

	.days-off-table th {
		text-align: left;
	}

</style>